/**
 * Acquisition de la position — EF-MOD-004, EF-MOD-005, EF-MOD-007.
 *
 * Les positions sont obtenues par un **flux** `watchPosition`, et non par des
 * appels ponctuels répétés : un flux laisse le système maintenir le récepteur
 * en veille active et regrouper les mises à jour, pour un coût en énergie
 * inférieur. Il est en outre la seule forme compatible avec EF-MOD-003.
 *
 * La position n'est ni enregistrée dans un historique, ni envoyée à un serveur.
 */

import type { Coordinate } from '../geo/geodesy';

/** Ancienneté maximale d'une position, en millisecondes. */
export const MAX_POSITION_AGE_MS = 60_000;

/** Incertitude horizontale maximale admise, en mètres. */
export const MAX_ACCURACY_METERS = 100;

/** Options du flux, fixées par EF-MOD-005. */
export const WATCH_OPTIONS: PositionOptions = {
  // Sans elle, le système renvoie des positions déduites du réseau, dont
  // l'incertitude dépasse couramment les 100 m admis : toutes seraient rejetées.
  enableHighAccuracy: true,
  // La fraîcheur est jugée par la règle des 60 secondes, et par elle seule.
  maximumAge: 0,
  timeout: 30_000,
};

export type PositionProblem = 'absente' | 'ancienne' | 'imprécise' | 'refusée' | 'indisponible';

export interface ValidPosition {
  readonly coordinate: Coordinate;
  readonly accuracyMeters: number;
  readonly timestampMs: number;
}

export type PositionOutcome =
  | { readonly kind: 'valid'; readonly position: ValidPosition }
  | { readonly kind: 'invalid'; readonly problem: PositionProblem; readonly message: string };

const PROBLEM_MESSAGES: Readonly<Record<PositionProblem, string>> = {
  absente: 'Position absente : aucune position n’a été obtenue.',
  ancienne: 'Position trop ancienne : elle date de plus de 60 secondes.',
  imprécise: 'Position trop imprécise : son incertitude dépasse 100 mètres.',
  refusée: 'Localisation refusée : les recalculs par position sont indisponibles.',
  indisponible:
    "La géolocalisation est indisponible : le mode course exige l'application installée en HTTPS.",
};

export function positionProblemMessage(problem: PositionProblem): string {
  return PROBLEM_MESSAGES[problem];
}

/**
 * Une position est **valide** lorsque ses trois conditions sont réunies :
 * coordonnées exploitables, horodatage de moins de 60 secondes, incertitude
 * horizontale d'au plus 100 mètres.
 *
 * La validité est indépendante de la présence sur le parcours (EF-MOD-006).
 */
export function evaluatePosition(position: GeolocationPosition, nowMs: number): PositionOutcome {
  const { latitude, longitude, accuracy } = position.coords;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { kind: 'invalid', problem: 'absente', message: PROBLEM_MESSAGES.absente };
  }
  if (nowMs - position.timestamp > MAX_POSITION_AGE_MS) {
    return { kind: 'invalid', problem: 'ancienne', message: PROBLEM_MESSAGES.ancienne };
  }
  if (!Number.isFinite(accuracy) || accuracy > MAX_ACCURACY_METERS) {
    return { kind: 'invalid', problem: 'imprécise', message: PROBLEM_MESSAGES.imprécise };
  }

  return {
    kind: 'valid',
    position: {
      coordinate: { lat: latitude, lon: longitude },
      accuracyMeters: accuracy,
      timestampMs: position.timestamp,
    },
  };
}

export interface GeolocationWatchOptions {
  readonly onOutcome: (outcome: PositionOutcome) => void;
  readonly now?: () => number;
  readonly geolocation?: Geolocation;
}

/**
 * Flux de positions. Un seul flux est actif à la fois ; il est arrêté à la
 * sortie du mode course et lorsque le document passe à l'état masqué, puis
 * rétabli au retour à l'état visible.
 */
export class PositionWatcher {
  private watchId: number | null = null;
  private readonly now: () => number;
  private readonly geolocation: Geolocation | undefined;

  constructor(private readonly options: GeolocationWatchOptions) {
    this.now = options.now ?? ((): number => Date.now());
    this.geolocation = options.geolocation ?? globalThis.navigator?.geolocation;
  }

  get isWatching(): boolean {
    return this.watchId !== null;
  }

  static get isSupported(): boolean {
    return globalThis.navigator?.geolocation !== undefined && globalThis.isSecureContext !== false;
  }

  /**
   * Démarre le flux. L'appel n'a lieu qu'à la suite d'une action explicite de
   * l'utilisateur activant le mode course (EF-MOD-004).
   */
  start(): void {
    if (this.watchId !== null) return;
    const geolocation = this.geolocation;
    if (geolocation === undefined) {
      this.options.onOutcome({
        kind: 'invalid',
        problem: 'indisponible',
        message: PROBLEM_MESSAGES.indisponible,
      });
      return;
    }

    this.watchId = geolocation.watchPosition(
      (position) => {
        this.options.onOutcome(evaluatePosition(position, this.now()));
      },
      (error) => {
        this.options.onOutcome({
          kind: 'invalid',
          problem: mapError(error),
          message: PROBLEM_MESSAGES[mapError(error)],
        });
      },
      WATCH_OPTIONS,
    );
  }

  stop(): void {
    if (this.watchId === null) return;
    this.geolocation?.clearWatch(this.watchId);
    this.watchId = null;
  }
}

function mapError(error: GeolocationPositionError): PositionProblem {
  // L'expiration du délai déclenche EF-MOD-007 avec la cause « position
  // absente », sans interrompre le flux.
  if (error.code === error.PERMISSION_DENIED) return 'refusée';
  if (error.code === error.TIMEOUT) return 'absente';
  return 'absente';
}
